import axios from 'axios';
import { store } from '../redux/store';

const BASE_URL = `${process.env.REACT_APP_BASE_URL}/communityV2`;

// Create axios instance with default configs
const api = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'uid': localStorage.getItem("userId"),
  },
});

// Request interceptor - can be used to add auth tokens
api.interceptors.request.use(
  (config) => {
    try {
      const state = store.getState();
      const uid = state?.projectInfo?.uid || localStorage.getItem("userId");
      const pubid = state?.projectInfo?.pubid;
      const dev_id = state?.projectInfo?.dev_id;
      const accessToken = state?.projectInfo?.accessToken;
      const device_type = state?.projectInfo?.device_type;
      const country_code = state?.projectInfo?.country_code;

      if (uid) {
        config.headers['uid'] = uid;
      }
      if (pubid) {
        config.headers['pubid'] = pubid;
      }
      if (dev_id) {
        config.headers['dev_id'] = dev_id;
      }
      if (device_type) {
        config.headers['device_type'] = device_type;
      }
      if (country_code) {
        config.headers['country_code'] = country_code;
      }

      // More descriptive logging with URL to identify which request is using which token
      if (accessToken) {
        config.headers['access-token'] = accessToken;
        // Visual indicator in console when token is applied
      } else {
        console.warn('⚠️ No access token available in Redux store for request to:', config.url);
      }
    } catch (error) {
      console.error('Error in request interceptor:', error);
    }

    return config;
  },
  (error) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor - for global error handling
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Global error handling
    console.error('API Error:', error);
    return Promise.reject(error);
  }
);

// Function to update the API instance with the latest token
export const updateApiToken = () => {
  try {
    const state = store.getState();
    const accessToken = state?.projectInfo?.accessToken;
    const currentToken = api.defaults.headers.common['access-token'];
    
    // Only update if the token has changed to avoid unnecessary operations
    if (accessToken && accessToken !== currentToken) {
      api.defaults.headers.common['access-token'] = accessToken;
      return true; // Token was updated
    } else if (!accessToken && currentToken) {
      delete api.defaults.headers.common['access-token'];
      return true; // Token was removed
    }
    return false; // No change needed
  } catch (error) {
    console.error('Error updating API token:', error);
    return false;
  }
};

// Initial token setup
updateApiToken();

/*
NOTE: We've removed the store.subscribe method to avoid running code on every Redux state change.
Instead, call updateApiToken() directly after dispatching actions that update the accessToken.

Example in your authentication logic:

dispatch(setProjectInfo({ ...data, accessToken: newToken }));
updateApiToken();
*/

// Add a diagnostic function to check token status
export const checkApiTokenStatus = () => {
  const state = store.getState();
  const storeToken = state?.projectInfo?.accessToken;
  const headerToken = api.defaults.headers.common['access-token'];
  const interceptorActive = api.interceptors.request.handlers.length > 0;
  
  return {
    storeToken,
    headerToken,
    interceptorActive,
    tokensMatch: storeToken === headerToken
  };
};

export default api;
